import {
  createContext,
  useContext,
  useRef,
  useCallback,
  useState,
  useEffect,
} from "react";

const STORAGE_KEY = "support-audio-playback-speed";
const VALID_SPEEDS = [0.5, 1, 1.5, 2];

/** Ref used by useTicket to skip 30s refresh while audio is playing (avoids cutting playback). */
export const isAudioPlayingRef = { current: false };

function readStoredSpeed() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v != null) {
      const n = Number(v);
      if (VALID_SPEEDS.includes(n)) return n;
    }
  } catch {
    // ignore
  }
  return 1;
}

const AudioPlayerContext = createContext(null);

/**
 * @typedef {Object} AudioTrack
 * @property {string} playerId
 * @property {string} url
 * @property {string} [senderName]
 * @property {string|number|null} [senderRemoteId]
 * @property {string|null} [senderAvatarUrl]
 * @property {string} [messageCreatedAt]
 * @property {string} [title]
 * @property {string} [subtitle]
 * @property {string|number} [ticketId]
 * @property {string|number} [messageId]
 * @property {string} [sortKey]
 */

/**
 * Only one audio file plays at a time across the app.
 * Playback is handled by GlobalAudioPlayerBar (persists across chat switches).
 */
export function useAudioPlayer() {
  return useContext(AudioPlayerContext);
}

/** زمان پخش با rAF — بدون پرش ناشی از timeupdate کم‌بازه */
export function useSmoothPlaybackTime(active = true, animating = false) {
  const ctx = useAudioPlayer();
  const playbackTimeRef = ctx?.playbackTimeRef;
  const fallback = ctx?.playback?.currentTime ?? 0;
  const [time, setTime] = useState(fallback);

  useEffect(() => {
    if (!active || !playbackTimeRef) {
      setTime(fallback);
      return undefined;
    }

    if (!animating) {
      setTime(playbackTimeRef.current);
      return undefined;
    }

    let rafId = 0;
    const tick = () => {
      setTime(playbackTimeRef.current);
      rafId = requestAnimationFrame(tick);
    };
    setTime(playbackTimeRef.current);
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [active, animating, playbackTimeRef, fallback]);

  return time;
}

export function AudioPlayerProvider({ children }) {
  const currentRef = useRef({ playerId: null, pause: null });
  const playersListRef = useRef([]);
  const engineRef = useRef(null);
  const nowPlayingRef = useRef(null);
  const playbackTimeRef = useRef(0);

  const [currentPlayerId, setCurrentPlayerId] = useState(null);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [playback, setPlayback] = useState({
    isPlaying: false,
    isLoading: false,
    currentTime: 0,
    duration: 0,
  });
  const [playbackSpeed, setPlaybackSpeedState] = useState(readStoredSpeed);
  // A round video message that was playing when its bubble went away: it carries on in a small circle.
  const [floatingVideo, setFloatingVideo] = useState(null);
  const floatingVideoTimeRef = useRef(0);
  const closeFloatingVideo = useCallback(() => setFloatingVideo(null), []);

  useEffect(() => {
    nowPlayingRef.current = nowPlaying;
  }, [nowPlaying]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(playbackSpeed));
    } catch {
      // ignore
    }
  }, [playbackSpeed]);

  const setPlaybackSpeed = useCallback((speed) => {
    const n = Number(speed);
    if (VALID_SPEEDS.includes(n)) {
      setPlaybackSpeedState(n);
      engineRef.current?.setRate?.(n);
    }
  }, []);

  const registerPlaybackEngine = useCallback((engine) => {
    engineRef.current = engine;
    return () => {
      if (engineRef.current === engine) {
        engineRef.current = null;
      }
    };
  }, []);

  const updatePlayback = useCallback((patch) => {
    if (typeof patch.currentTime === "number" && Number.isFinite(patch.currentTime)) {
      playbackTimeRef.current = patch.currentTime;
    }
    setPlayback((prev) => {
      const next = { ...prev, ...patch };
      if (patch.isPlaying !== undefined) {
        isAudioPlayingRef.current = patch.isPlaying;
      }
      return next;
    });
  }, []);

  const clearCurrentPlayer = useCallback(() => {
    currentRef.current = { playerId: null, pause: null };
    setCurrentPlayerId(null);
    setPlayback((prev) => ({ ...prev, isPlaying: false }));
    isAudioPlayingRef.current = false;
  }, []);

  const stopPlayback = useCallback(() => {
    engineRef.current?.pause?.();
    clearCurrentPlayer();
    playbackTimeRef.current = 0;
    setNowPlaying(null);
    setPlayback({
      isPlaying: false,
      isLoading: false,
      currentTime: 0,
      duration: 0,
    });
  }, [clearCurrentPlayer]);

  const endCurrentTrack = useCallback(() => {
    engineRef.current?.pause?.();
    setPlayback((prev) => ({
      ...prev,
      isPlaying: false,
      isLoading: false,
    }));
    isAudioPlayingRef.current = false;
  }, []);

  const pauseCurrent = useCallback(() => {
    engineRef.current?.pause?.();
    clearCurrentPlayer();
  }, [clearCurrentPlayer]);

  const togglePlayPause = useCallback(() => {
    if (!nowPlayingRef.current) return;
    engineRef.current?.toggle?.();
  }, []);

  const seekPlayback = useCallback((ratio) => {
    const clamped = Math.min(1, Math.max(0, Number(ratio) || 0));
    engineRef.current?.seek?.(clamped);
  }, []);

  const openFloatingVideo = useCallback(
    (track) => {
      if (!track?.url) return;
      stopPlayback();
      floatingVideoTimeRef.current = track.startAt || 0;
      setFloatingVideo(track);
    },
    [stopPlayback]
  );

  const playTrack = useCallback(
    (track) => {
      if (!track?.playerId || !track?.url) return;
      setFloatingVideo(null);

      const prev = currentRef.current;
      if (
        prev.playerId !== null &&
        prev.playerId !== track.playerId &&
        typeof prev.pause === "function"
      ) {
        prev.pause();
      }

      const isSameTrack = nowPlayingRef.current?.playerId === track.playerId;
      setNowPlaying(track);
      currentRef.current = {
        playerId: track.playerId,
        pause: () => engineRef.current?.pause?.(),
      };
      setCurrentPlayerId(track.playerId);
      isAudioPlayingRef.current = true;

      if (isSameTrack) {
        engineRef.current?.toggle?.();
        return;
      }

      playbackTimeRef.current = 0;
      setPlayback((prev) => ({
        ...prev,
        isLoading: true,
        isPlaying: false,
        currentTime: 0,
      }));
    },
    []
  );

  const stopCurrentAndRegister = useCallback(
    (playerId, pauseFn) => {
      const prev = currentRef.current;
      if (prev.playerId !== null && prev.playerId !== playerId && typeof prev.pause === "function") {
        prev.pause();
      }
      currentRef.current = { playerId, pause: pauseFn };
      setCurrentPlayerId(playerId);
      isAudioPlayingRef.current = true;
    },
    []
  );

  const registerPlayer = useCallback((playerId, { pause, play, sortKey: rawSortKey }) => {
    const list = playersListRef.current;
    const sortKey = rawSortKey != null ? String(rawSortKey) : playerId;
    const idx = list.findIndex((p) => p.playerId === playerId);
    const entry = { playerId, pause, play, sortKey };
    if (idx >= 0) {
      list[idx] = entry;
    } else {
      list.push(entry);
    }
  }, []);

  const unregisterPlayer = useCallback((playerId) => {
    playersListRef.current = playersListRef.current.filter((p) => p.playerId !== playerId);
  }, []);

  const playNextAfter = useCallback(
    (playerId) => {
      const list = playersListRef.current;
      const sorted = [...list].sort((a, b) =>
        (a.sortKey || a.playerId).localeCompare(b.sortKey || b.playerId)
      );
      const idx = sorted.findIndex((p) => p.playerId === playerId);
      if (idx < 0 || idx >= sorted.length - 1) {
        endCurrentTrack();
        return;
      }
      const next = sorted[idx + 1];
      if (next && typeof next.play === "function") {
        next.play();
      } else {
        endCurrentTrack();
      }
    },
    [endCurrentTrack]
  );

  const unregister = useCallback(
    (playerId) => {
      unregisterPlayer(playerId);
    },
    [unregisterPlayer]
  );

  return (
    <AudioPlayerContext.Provider
      value={{
        stopCurrentAndRegister,
        unregister,
        registerPlayer,
        unregisterPlayer,
        playNextAfter,
        clearCurrentPlayer,
        pauseCurrent,
        stopPlayback,
        togglePlayPause,
        seekPlayback,
        playTrack,
        registerPlaybackEngine,
        updatePlayback,
        playbackTimeRef,
        currentPlayerId,
        nowPlaying,
        playback,
        playbackSpeed,
        setPlaybackSpeed,
        floatingVideo,
        floatingVideoTimeRef,
        openFloatingVideo,
        closeFloatingVideo,
      }}
    >
      {children}
    </AudioPlayerContext.Provider>
  );
}
